import api from './api';

class NotificationPreferencesService {
  constructor() {
    this.preferences = null;
    this.loading = false;
  }

  // Get user notification preferences (cached)
  async getPreferences() {
    if (this.preferences && !this.loading) {
      return this.preferences;
    }

    this.loading = true;
    try {
      const response = await api.get('/api/notification-preferences');
      this.preferences = response.data;
      return this.preferences;
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
      // Return default preferences on error
      return this.getDefaultPreferences();
    } finally {
      this.loading = false;
    }
  }

  // Update user notification preferences
  async updatePreferences(preferences) {
    try {
      const response = await api.put('/api/notification-preferences', preferences);
      this.preferences = response.data;
      return this.preferences;
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      throw error;
    }
  }

  // Reset to default preferences
  async resetToDefaults() {
    try {
      const response = await api.post('/api/notification-preferences/reset');
      this.preferences = response.data;
      return this.preferences;
    } catch (error) {
      console.error('Failed to reset notification preferences:', error);
      throw error;
    }
  }

  // Check if browser notifications are enabled for a specific type
  async canShowBrowserNotification(type = 'general') {
    try {
      const preferences = await this.getPreferences();
      
      // Check if browser notifications are globally enabled
      if (!preferences?.browser?.enabled) {
        return false;
      }

      // Check type-specific preference
      if (preferences?.browser?.types && typeof preferences.browser.types[type] === 'boolean') {
        return preferences.browser.types[type];
      }

      // Default to enabled if type-specific preference not set
      return true;
    } catch (error) {
      console.error('Error checking browser notification preference:', error);
      // Default to enabled on error
      return true;
    }
  }

  // Check if email notifications are enabled for a specific type
  async canSendEmail(type = 'general') {
    try {
      const preferences = await this.getPreferences();
      
      if (!preferences?.email?.enabled) {
        return false;
      }

      if (preferences?.email?.types && typeof preferences.email.types[type] === 'boolean') {
        return preferences.email.types[type];
      }

      return true;
    } catch (error) {
      console.error('Error checking email notification preference:', error);
      return true;
    }
  }

  // Check if in-app notifications are enabled for a specific type
  async canShowInApp(type = 'general') {
    try {
      const preferences = await this.getPreferences();
      
      if (!preferences?.inApp?.enabled) {
        return false;
      }

      if (preferences?.inApp?.types && typeof preferences.inApp.types[type] === 'boolean') {
        return preferences.inApp.types[type];
      }

      return true;
    } catch (error) {
      console.error('Error checking in-app notification preference:', error);
      return true;
    }
  }

  // Clear cached preferences (useful when user logs out)
  clearCache() {
    this.preferences = null;
  }

  // Get default preferences structure
  getDefaultPreferences() {
    return {
      email: {
        enabled: true,
        types: {
          bid_placed: true,
          outbid: true,
          auction_ended: true,
          new_offer: true,
          message: true,
          general: true
        }
      },
      browser: {
        enabled: true,
        types: {
          bid_placed: true,
          outbid: true,
          auction_ended: true,
          new_offer: true,
          message: true,
          general: true
        }
      },
      inApp: {
        enabled: true,
        types: {
          bid_placed: true,
          outbid: true,
          auction_ended: true,
          new_offer: true,
          message: true,
          general: true
        }
      }
    };
  }
}

// Create singleton instance
const notificationPreferences = new NotificationPreferencesService();

export default notificationPreferences;